import { useAbortController } from 'utils/hooks';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import DimensionHelmet from 'components/DimensionHelmet';
import GoogleCategoryThree from './Google';
import IfHasTikTokAccess from 'components/NoAccessMessage/IfHasTikTokAccess';
import InstagramCategoryThree from './Instagram';
import OverviewCategoryThree from './Overview';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import TikTokCategoryThree from './TikTok';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';
import useEntityAcrossSources from 'utils/hooks/useEntityAcrossSources';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import { useParams } from 'react-router';

function CategoryThree() {
    const { mode, modes, dataSource } = useDataSourceToggle();
    const params = useParams();
    const { categoryId } = params;
    const controller = useAbortController(categoryId);
    // Source-independent: the page title and the source tabs describe the
    // entity, not one source's view of it, and must survive a bind being down.
    // Each tab resolves its own entity for its own content and breadcrumb.
    const { entity, entityBySource } = useEntityAcrossSources({
        id: categoryId,
        entityType: 'categories',
        dataSource,
    });
    const { name, type, hierarchies } = entity || {};
    const getHierarchy = useGetHierarchy();
    const { hierarchy_hint } = getHierarchy(hierarchies);
    // Per source, so each tab is judged on the taxonomy it would actually query.
    const hierarchyHintBySource = Object.fromEntries(
        Object.entries(entityBySource).map(([source, sourceEntity]) => [
            source,
            getHierarchy(sourceEntity?.hierarchies).hierarchy_hint,
        ]),
    );

    // The tabs describe the entity rather than one source's view of it, so this
    // builds them -- but each view renders them beneath its own breadcrumbs.
    const tabs = (
        <DataSourceTabsByCoverage
            entityId={categoryId}
            routePrefix="/categoryThree"
            hierarchyHint={hierarchy_hint}
            hierarchyHintBySource={hierarchyHintBySource}
        />
    );

    return (
        <PagePadding>
            <PageBar />
            <Stack $space="1.125rem">
                <DimensionHelmet
                    name={name}
                    type={type}
                    hierarchy_hint={hierarchy_hint}
                />
                <Stack $space="1.25rem">
                    {mode === modes.OVERVIEW ? (
                        <OverviewCategoryThree
                            categoryId={categoryId}
                            controller={controller}
                            tabs={tabs}
                        />
                    ) : null}
                    {mode === modes.SEARCH ? (
                        <GoogleCategoryThree
                            categoryId={categoryId}
                            controller={controller}
                            tabs={tabs}
                        />
                    ) : null}
                    {mode === modes.TIKTOK ? (
                        <IfHasTikTokAccess header={tabs}>
                            <TikTokCategoryThree
                                categoryId={categoryId}
                                controller={controller}
                                tabs={tabs}
                            />
                        </IfHasTikTokAccess>
                    ) : null}
                    {mode === modes.INSTAGRAM ? (
                        <InstagramCategoryThree
                            categoryId={categoryId}
                            controller={controller}
                            tabs={tabs}
                        />
                    ) : null}
                </Stack>
            </Stack>
        </PagePadding>
    );
}

export default CategoryThree;
